import React, { createContext, useContext, useState, useEffect } from 'react';

type FontSize = 'small' | 'medium' | 'large';
type Theme = 'light' | 'dark';

interface AppearanceContextType {
  fontSize: FontSize;
  theme: Theme;
  /** Numeric scale for global font sizing (e.g., 0.85 - 1.3). Applied to --app-font-scale */
  fontScale: number;
  setFontSize: (size: FontSize) => void;
  setTheme: (theme: Theme) => void;
  setFontScale: (scale: number) => void;
}

const AppearanceContext = createContext<AppearanceContextType | undefined>(undefined);

export const useAppearance = () => {
  const context = useContext(AppearanceContext);
  if (!context) {
    throw new Error('useAppearance must be used within an AppearanceProvider');
  }
  return context;
};

interface AppearanceProviderProps {
  children: React.ReactNode;
}

export const AppearanceProvider: React.FC<AppearanceProviderProps> = ({ children }) => {
  const [fontSize, setFontSize] = useState<FontSize>('medium');
  const [theme, setTheme] = useState<Theme>('light');
  const [fontScale, setFontScale] = useState<number>(1);

  // Aplicar tamanho da fonte ao elemento raiz para afetar todo o conteúdo
  useEffect(() => {
    const rootElement = document.documentElement;
    
    // Remover classes anteriores de tamanho de fonte
    rootElement.classList.remove('font-size-small', 'font-size-medium', 'font-size-large');
    
    // Adicionar nova classe de tamanho de fonte
    rootElement.classList.add(`font-size-${fontSize}`);
    
    // Salvar no localStorage para persistência
    localStorage.setItem('app-font-size', fontSize);
  }, [fontSize]);

  // Aplicar escala numérica de fonte via CSS variable
  useEffect(() => {
    const rootElement = document.documentElement;
    // Inline style has higher priority than class-level variable
    rootElement.style.setProperty('--app-font-scale', String(fontScale));
    localStorage.setItem('app-font-scale', String(fontScale));
    // eslint-disable-next-line no-console
    console.log('[AppearanceContext] Saving fontScale to localStorage:', fontScale);
  }, [fontScale]);

  // Aplicar tema
  useEffect(() => {
    const rootElement = document.documentElement;
    
    if (theme === 'dark') {
      rootElement.classList.add('dark');
    } else {
      rootElement.classList.remove('dark');
    }
    
    // Salvar no localStorage para persistência
    localStorage.setItem('app-theme', theme);
  }, [theme]);

  // Carregar configurações do localStorage na inicialização
  useEffect(() => {
    const savedFontSize = localStorage.getItem('app-font-size') as FontSize | null;
    const savedTheme = localStorage.getItem('app-theme') as Theme | null;
    const savedFontScaleRaw = localStorage.getItem('app-font-scale');

    // eslint-disable-next-line no-console
    console.log('[AppearanceContext] Loading from localStorage:', {
      savedFontSize,
      savedTheme,
      savedFontScaleRaw,
      parsedFontScale: savedFontScaleRaw ? Number(savedFontScaleRaw) : null
    });

    if (savedFontSize) {
      setFontSize(savedFontSize);
    }

    if (savedTheme) {
      setTheme(savedTheme);
    }

    if (savedFontScaleRaw) {
      const parsed = Number(savedFontScaleRaw);
      if (!Number.isNaN(parsed) && parsed > 0) {
        setFontScale(parsed);
        // eslint-disable-next-line no-console
        console.log('[AppearanceContext] Loaded fontScale:', parsed);
      }
    }
  }, []);

  const value = {
    fontSize,
    theme,
    fontScale,
    setFontSize,
    setTheme,
    setFontScale,
  };

  return (
    <AppearanceContext.Provider value={value}>
      {children}
    </AppearanceContext.Provider>
  );
};
